import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sprout, Phone, KeyRound, Shield, ArrowLeft, Download, Share2 } from 'lucide-react';
import { toast } from 'sonner';

function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsInstalled(standalone);
    if (standalone) return;

    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()) && !window.MSStream;
    setIsIOS(ios);

    if (ios) { setShow(true); return; }

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setShow(true); };
    window.addEventListener('beforeinstallprompt', handler);

    const isMobile = /android|webos|blackberry|iemobile|opera mini/i.test(navigator.userAgent.toLowerCase());
    if (isMobile) setShow(true);

    window.addEventListener('appinstalled', () => { setIsInstalled(true); setShow(false); });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) { deferredPrompt.prompt(); await deferredPrompt.userChoice; setDeferredPrompt(null); setShow(false); }
  };

  if (isInstalled || !show) return null;

  return (
    <div data-testid="install-cta" className="bg-[#0B4F3F] rounded-2xl p-4 text-[#F6F8FB] mb-4 animate-fade-in-up">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#F6F8FB]/20 flex items-center justify-center flex-shrink-0">
          {isIOS ? <Share2 className="w-6 h-6 text-[#F6F8FB]" /> : <Download className="w-6 h-6 text-[#F6F8FB]" />}
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold">Get the Frootex App</p>
          {isIOS ? (
            <p className="text-xs text-[#F6F8FB]/80 mt-0.5">
              Tap <Share2 className="w-3 h-3 inline" /> then "Add to Home Screen"
            </p>
          ) : deferredPrompt ? (
            <p className="text-xs text-[#F6F8FB]/80 mt-0.5">Install for faster access, even offline</p>
          ) : (
            <p className="text-xs text-[#F6F8FB]/80 mt-0.5">Open in Chrome and tap "Install"</p>
          )}
        </div>
        {!isIOS && deferredPrompt && (
          <button
            data-testid="install-app-btn"
            onClick={handleInstall}
            className="px-4 py-2 bg-[#F6F8FB] text-[#0B4F3F] text-xs font-bold rounded-full active:scale-95 transition-transform flex-shrink-0"
          >
            Install
          </button>
        )}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { sendOtp, verifyOtp, adminLogin } = useAuth();
  const [mode, setMode] = useState('phone'); // phone, otp, admin
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoOtp, setDemoOtp] = useState(null);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      toast.error('Enter a valid phone number');
      return;
    }
    setLoading(true);
    try {
      const formatted = phone.startsWith('+') ? phone : `+91${phone}`;
      const res = await sendOtp(formatted, 'recaptcha-container');
      setPhone(formatted);
      setMode('otp');
      if (res?.provider === 'mock' && res?.mock_otp) {
        setDemoOtp(res.mock_otp);
        toast.success(`Demo mode: your OTP is ${res.mock_otp}`);
      } else if (res?.provider === 'mock') {
        setDemoOtp(null);
        toast.success('OTP sent — check your SMS');
      } else {
        setDemoOtp(null);
        toast.success('OTP sent to your phone!');
      }
    } catch (err) {
      console.error('OTP error:', err);
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch { /* noop */ }
        window.recaptchaVerifier = null;
      }
      const code = err?.code;
      let msg;
      if (code === 'auth/too-many-requests') {
        msg = 'Too many attempts. Try again in a few minutes.';
      } else if (code === 'auth/invalid-phone-number') {
        msg = 'Invalid phone number — please include country code (e.g. +91)';
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err?.message?.includes('Request failed')) {
        msg = 'Connection issue. Please check your internet and try again.';
      } else {
        msg = err?.message || 'Failed to send OTP. Please try again.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Enter a valid 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(phone, otp);
      toast.success('Login successful!');
    } catch (err) {
      console.error('Verify error:', err);
      const code = err?.code;
      let msg;
      if (code === 'auth/invalid-verification-code') {
        msg = 'Invalid OTP. Please double-check the code.';
      } else if (code === 'auth/code-expired') {
        msg = 'OTP expired. Please request a new one.';
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err?.message?.includes('Request failed')) {
        msg = 'Connection issue. Please check your internet and try again.';
      } else {
        msg = err?.message || 'Verification failed. Please try again.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success('Admin login successful!');
      // App.js /login route auto-redirects admin sessions to /admin
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] flex flex-col" data-testid="login-page">
      {/* Invisible reCAPTCHA container */}
      <div id="recaptcha-container"></div>

      <div className="w-full max-w-md mx-auto flex-1 flex flex-col p-6">
        {/* Header */}
        <div className="pt-12 pb-6 text-center animate-fade-in-up">
          <img
            src="/frootex-logo.png"
            alt="Frootex"
            className="mx-auto mb-4 w-48 sm:w-56 h-auto"
            data-testid="brand-logo"
          />
          <p className="text-[#5B6675] mt-2 text-base">
            Fresh from farm to your business
          </p>
        </div>

        {/* Install CTA */}
        <InstallBanner />

        {/* Phone Input Step */}
        {mode === 'phone' && (
          <div className="flex-1 flex flex-col animate-fade-in-up stagger-1">
            <form onSubmit={handleSendOtp} className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6675]" />
                  <Input
                    data-testid="phone-input"
                    type="tel"
                    placeholder="Enter your mobile number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-11 h-12 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                  />
                </div>
                <p className="text-xs text-[#5B6675] mt-1.5">We'll send you a verification code via SMS</p>
              </div>

              <Button
                data-testid="send-otp-btn"
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold text-base transition-colors"
              >
                {loading ? 'Sending OTP...' : 'Get OTP'}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <button
                data-testid="admin-login-toggle"
                onClick={() => setMode('admin')}
                className="text-sm text-[#5B6675] hover:text-[#0B4F3F] transition-colors inline-flex items-center gap-1.5"
              >
                <Shield className="w-3.5 h-3.5" />
                Admin Login
              </button>
            </div>
          </div>
        )}

        {/* OTP Verification Step */}
        {mode === 'otp' && (
          <div className="flex-1 flex flex-col animate-fade-in-up">
            <button
              onClick={() => {
                setMode('phone');
                setOtp('');
                setDemoOtp(null);
                if (window.recaptchaVerifier) {
                  try { window.recaptchaVerifier.clear(); } catch {}
                  window.recaptchaVerifier = null;
                }
              }}
              className="flex items-center gap-1.5 text-sm text-[#5B6675] mb-6 hover:text-[#0B4F3F] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Change number
            </button>

            {demoOtp && (
              <div
                data-testid="demo-otp-banner"
                className="mb-5 rounded-xl border border-[#0B4F3F]/20 bg-[#0B4F3F]/5 px-4 py-3 text-sm text-[#0B1620]"
              >
                <p className="font-semibold text-[#0B4F3F]">Demo mode active</p>
                <p className="mt-0.5 text-[#5B6675]">
                  SMS delivery isn't available on this domain. Use this code:{' '}
                  <span className="font-mono text-base font-bold text-[#0B1620]" data-testid="demo-otp-code">{demoOtp}</span>
                </p>
              </div>
            )}

            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Enter OTP</Label>
                <p className="text-xs text-[#5B6675] mb-3">Sent to {phone}</p>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6675]" />
                  <Input
                    data-testid="otp-input"
                    type="text"
                    maxLength={6}
                    placeholder="6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="pl-11 h-12 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] text-center text-lg tracking-[0.3em] font-semibold placeholder:text-[#5B6675]/60 placeholder:tracking-normal placeholder:text-base placeholder:font-normal focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                  />
                </div>
              </div>

              <Button
                data-testid="verify-otp-btn"
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold text-base transition-colors"
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
            </form>
          </div>
        )}

        {/* Admin Login */}
        {mode === 'admin' && (
          <div className="flex-1 flex flex-col animate-fade-in-up">
            <button
              onClick={() => setMode('phone')}
              className="flex items-center gap-1.5 text-sm text-[#5B6675] mb-6 hover:text-[#0B4F3F] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to phone login
            </button>

            <form onSubmit={handleAdminLogin} className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Email</Label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  placeholder="admin@frootex.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Password</Label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
                />
              </div>
              <Button
                data-testid="admin-login-btn"
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold text-base transition-colors"
              >
                {loading ? 'Logging in...' : 'Admin Login'}
              </Button>
            </form>
          </div>
        )}

        {/* Footer */}
        <div className="py-6 text-center">
          <p className="text-xs text-[#5B6675]/60">
            By continuing, you agree to our Terms & Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
